import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Form, Grid, Icon, Loader, Message, Segment, Tab, TextArea, Label } from 'semantic-ui-react'
import { Field, Form as FinalForm } from 'react-final-form'
import arrayMutators from 'final-form-arrays'
import { FieldArray } from 'react-final-form-arrays'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { FileDrop, ImageList, uploadFileToS3, useImageList } from '@iheartjane/react-image-list'
import { ResponsiveImage } from '@iheartjane/react-responsive-image'

import { Brand, ProductForm } from '../../redux/types/products'
import {
  ArrayFieldLabelStyle,
  ProductsSegmentStyle,
  ProductTitleStyle,
  StyledProductImage,
  StyledProductImagesHolder,
} from './styles'
import {
  cancelAProductRemoval,
  getAProduct,
  getBrands,
  initiateAProductRemoval,
  postAProduct,
} from '../../redux/actions/products'
import { setInitialProductValues, setIsPostingProduct, setLastAction } from '../../redux/actions/ui'
import { getInitialProductFormValues } from '../../redux/reducers/ui'
import { SelectionOptions } from '../SelectionOptions/SelectionOptions'
import { AppState } from '../../redux/store'
import {
  API_S3_UPLOAD,
  LINEAGE_OPTIONS,
  PATH_PRODUCTS_CREATE,
  PATH_PRODUCTS_EDIT,
  PATH_ROOT,
  PRODUCT_SUBTYPE_OPTIONS,
  PRODUCT_TYPE_OPTIONS,
  QUANTITY_UNIT_OPTIONS,
  SWITCH_TO_CREATE,
  SWITCH_TO_EDIT,
} from '../../consts'
import { objectFlattener } from '../../utils/objectFlattener'
import { UnsavedWarningModal } from './UnsavedWarningModal'
import { ProductKind } from '../../types'
import { RemoveProductModal } from './RemoveProductModal'
import { BrandSearch } from '../BrandSearch/BrandSearch'
import { CreateNoticeModal } from '../CreateNoticeModal/CreateNoticeModal'
import { BrandSearchAutoMatch } from '../BrandSearch/BrandSearchAutoMatch'
import { Localizations } from '../Localizations/Localizations'
import LocaleSpecificGridView from '../Localizations/LocaleSpecificGridView'

interface TextNumberInputProps {
  label: string
  props: any
  placeholder?: string
  required?: boolean
  min?: string
  max?: string
  step?: string
  icon?: any
  fullWidth?: boolean
  twoLine?: boolean
}

export const productSrcWidths = [100, 200, 400, 800, 1000]

interface ProductsProps {
  handleBatchModeCreateModalClose?: () => void
  batchCreationIndex?: number
  locale?: string
}

export const ProductImages = ({
  onChange,
  initialProductValues,
}: {
  onChange: any
  initialProductValues: any
}) => {
  const { initialImages, images, addImage, removeImage, resetImages } = useImageList()
  const filePicker = useRef<HTMLInputElement | null>(null)

  const initialImagesJSON = JSON.stringify(initialImages)
  useEffect(() => resetImages(), [initialImagesJSON]) // eslint-disable-line
  useEffect(() => resetImages(), [JSON.stringify(initialProductValues)]) // eslint-disable-line

  useEffect(() => {
    onChange(images)
  }, [JSON.stringify(images), onChange]) // eslint-disable-line

  const addFiles = (files: FileList) => {
    ;[...files].forEach(async (file) => {
      const url = await uploadFileToS3(API_S3_UPLOAD, file, {
        credentials: 'include',
      })
      addImage(url)
    })
  }

  const addImageClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    filePicker.current?.click()
  }

  const onFilesPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files)
    }
  }

  return (
    <FileDrop onFilesAdded={addFiles}>
      <ArrayFieldLabelStyle>Photos</ArrayFieldLabelStyle>
      <input
        type="file"
        value={''}
        multiple
        style={{ display: 'none' }}
        onChange={onFilesPicked}
        ref={filePicker}
      />
      <button
        type="button"
        style={{ marginLeft: 10, display: 'inline-block' }}
        onClick={addImageClick}
      >
        <Icon name="add" size="small" />
        Add
      </button>

      <ImageList.Images axis="y" useDragHandle>
        <StyledProductImagesHolder>
          {images?.map((image: string, index: any) => (
            <ImageList.Image key={image} index={index}>
              <div style={{ position: 'relative' }}>
                <ImageList.ImageHandle>
                  {image && (
                    <ResponsiveImage
                      className="ui small bordered image"
                      style={{ borderRadius: '5px', pointerEvents: 'none' }}
                      src={image}
                      srcWidths={productSrcWidths}
                      fallbackWidth={200}
                      alt=""
                    />
                  )}
                </ImageList.ImageHandle>
                <StyledProductImage>
                  <a href={image} target="_blank" rel="noopener noreferrer">
                    View Source
                  </a>
                  <Icon
                    name="trash alternate outline"
                    link
                    onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => {
                      removeImage(image)
                    }}
                  />
                </StyledProductImage>
              </div>
            </ImageList.Image>
          ))}
        </StyledProductImagesHolder>
      </ImageList.Images>
    </FileDrop>
  )
}

const Products = (props: ProductsProps) => {
  const { handleBatchModeCreateModalClose } = props
  const source = useSelector((state: AppState) => state.products.brands)
  const [unsavedWarning, setUnsavedWarning] = useState(false)
  const [removeConfirmation, setRemoveConfirmation] = useState(false)
  const createProductFormRef = useRef<HTMLFormElement>(null)
  const dispatch = useDispatch()
  const location = useLocation()
  const { isPostingProduct, initialProductValues, batchLookupStage } = useSelector(
    (state: AppState) => state.ui
  )
  const mode =
    batchLookupStage === 'create'
      ? 'create'
      : (location.pathname.split('/').pop() as 'create' | 'edit')
  const isBatchLookup = typeof handleBatchModeCreateModalClose === 'function'

  const removalStatus = initialProductValues?.removal_status
  const noticeStatus = initialProductValues?.notice_status
  const storeCount = initialProductValues?.store_count
  const states = initialProductValues?.states

  const localizations = useMemo(
    () => <Localizations productId={initialProductValues.id?.toString()} mode={mode} />,
    [initialProductValues.id, mode]
  )

  useEffect(() => {
    dispatch(getBrands())
  }, [dispatch])

  useEffect(() => {
    dispatch(setLastAction({ mode: mode }))
  }, [dispatch, mode])
  const onSubmit = async (values: ProductForm) => {
    dispatch(setIsPostingProduct({ status: true }))

    if (values.kind === 'flower' && !values.category) {
      toast('Lineage is required on Flower type', {
        containerId: 'error',
      })
      dispatch(setIsPostingProduct({ status: false }))
      return
    }

    if (values.kind === 'flower' && !values.root_subtype) {
      toast('Subtype is required on Flower type; use Flower/Flower', {
        containerId: 'error',
      })
      dispatch(setIsPostingProduct({ status: false }))
      return
    }

    values = {
      ...getInitialProductFormValues(),
      ...values,
      copy_attributes_from_product_id: initialProductValues.id,
    }

    // flatten array fields, e.g. aliases_attributes, etc.
    const formValues = objectFlattener(
      values,
      (mode === 'edit' ? [] : ['id']).concat(
        // don't send the brand key if we have a product_brand_id present
        values.product_brand_id !== undefined ? ['brand'] : []
      )
    )

    const formData = new FormData()

    if (values.product_brand_id !== undefined) {
      const correspondingBrand = source.find(
        (b: Brand) => b.id === parseInt(values.product_brand_id)
      )
      if (correspondingBrand) {
        formData.append('brand', correspondingBrand.name)
      } else {
        formData.append('brand', '')
      }
    }

    Object.keys(formValues).forEach((key) => {
      // photos_attributes is a deprecated attribute, and image_urls are handled below
      if (key.startsWith('photos_attributes') || key.startsWith('image_urls')) {
        return
      }
      formData.append(key, formValues[key])
    })

    if (values.image_urls.length) {
      values.image_urls.forEach((image: string) => {
        formData.append(`image_urls[]`, image)
      })
    } else {
      formData.append('image_urls', '[]')
    }

    dispatch(
      postAProduct({
        formData: formData,
        mode: mode,
        isBatchLookup: isBatchLookup,
        batchCreationIndex: props.batchCreationIndex,
      })
    )
  }

  const TextNumberInput = (inputProps: TextNumberInputProps) => {
    return (
      <Form.Field inline>
        <label className="product-form-label">
          {`${inputProps.label}${inputProps.required ? '*' : ''}`}
        </label>
        {inputProps?.twoLine && <br />}
        <input
          required={inputProps.required}
          placeholder={inputProps.placeholder}
          min={inputProps.min ? inputProps.min : undefined}
          max={inputProps.max ? inputProps.max : undefined}
          step={inputProps.step ? inputProps.step : undefined}
          {...inputProps.props.input}
          style={inputProps.icon && { marginRight: '10px' }}
          className={inputProps.fullWidth ? 'full-width' : ''}
        />
        {inputProps.icon}
      </Form.Field>
    )
  }

  const getModeToggle = () => {
    // Returns a toggle to switch between edit/create modes of product if a product is currently "in-view",
    // otherwise, returns null.
    const showToggle = ['edit', 'create'].includes(mode)

    if (!showToggle) {
      return null
    }

    return (
      <Link
        to={mode === 'edit' ? PATH_PRODUCTS_CREATE : PATH_PRODUCTS_EDIT}
        className="ui button secondary"
      >
        {mode === 'edit' ? SWITCH_TO_CREATE : SWITCH_TO_EDIT}
      </Link>
    )
  }

  const getBrandField = () => {
    return (
      <Field name="product_brand_id">
        {({ input, meta, options }) => {
          if (props.batchCreationIndex === undefined || props.batchCreationIndex === null) {
            return (
              <BrandSearch
                as={Form.Field}
                placeholder="Search brands.."
                name={input.name}
                onSelect={(result: Brand | null) => {
                  if (result) {
                    input.onChange(result.id)
                  } else {
                    input.onChange('')
                  }
                }}
                source={source}
                currentBrandId={input.value !== '' && input.value}
              />
            )
          } else {
            return (
              <BrandSearchAutoMatch
                as={Form.Field}
                initialBrand={initialProductValues.brand}
                placeholder="Search brands.."
                name={input.name}
                onSelect={(result: Brand | null) => {
                  if (result) {
                    input.onChange(result.id)
                  } else {
                    input.onChange('')
                  }
                }}
                source={source}
                currentBrandId={input.value ?? ''}
              />
            )
          }
        }}
      </Field>
    )
  }

  return (
    <ProductsSegmentStyle basic padded>
      <Segment padded basic>
        <div style={{ marginBottom: 10 }}>
          <ProductTitleStyle>
            {mode === 'edit'
              ? initialProductValues.name
                ? `Edit ${initialProductValues.name} (id: ${initialProductValues.id})`
                : 'Edit a product'
              : initialProductValues.name
              ? `Create a product like ${initialProductValues.name} (id: ${initialProductValues.id})`
              : 'Create a product'}
          </ProductTitleStyle>

          {mode === 'edit' && initialProductValues.id ? (
            <CreateNoticeModal productId={initialProductValues?.id?.toString()} />
          ) : null}

          {batchLookupStage !== 'create' && (
            <FinalForm
              onSubmit={(values) => {
                if (values.product_id != null) {
                  const productId = values.product_id.toString()
                  dispatch(getAProduct({ productId: productId, mode: mode }))
                }
              }}
              initialValues={{ product_id: initialProductValues.id }}
              render={({ handleSubmit }) => (
                <Form onSubmit={handleSubmit} style={{ display: 'inline-block' }}>
                  <Field name="product_id" type="number">
                    {(props) => (
                      <Form.Input
                        {...props.input}
                        action={{ icon: 'search' }}
                        placeholder="Quick id lookup"
                        min="0"
                        required
                      />
                    )}
                  </Field>
                </Form>
              )}
            />
          )}
        </div>

        {removalStatus != null ? (
          <Message warning icon className="small-icon">
            <Icon name="trash alternate" />
            <Message.Content>
              {removalStatus === 'pending'
                ? 'This product is marked for removal and will be removed when it goes out of stock everywhere.'
                : 'This product is removed. This is irreversible.'}
            </Message.Content>
          </Message>
        ) : null}

        {noticeStatus != null ? (
          <Message color="blue" icon className="small-icon">
            <Icon name="angle right" />
            <Message.Content>
              {
                {
                  duplicate: 'This product was reported as a duplicate of another product.',
                  discontinued: 'This product was reported as discontinued.',
                }[noticeStatus]
              }
            </Message.Content>
          </Message>
        ) : null}

        {mode === 'edit' && !initialProductValues.id ? (
          <React.Fragment>
            <Message>
              Enter a product id or select a product from <Link to={PATH_ROOT}>Lookup</Link>.
            </Message>

            <div>{getModeToggle()}</div>
          </React.Fragment>
        ) : (
          <FinalForm
            onSubmit={onSubmit}
            mutators={{
              ...arrayMutators,
              clearRootSubType: (args, state, utils) => {
                utils.changeValue(state, 'root_subtype', () => '')
              },
              titleCase: ([name], state, { changeValue }) => {
                changeValue(
                  state,
                  name,
                  (value) =>
                    value &&
                    value
                      .split(' ')
                      .map((w: string) => w[0].toUpperCase() + w.substr(1).toLowerCase())
                      .join(' ')
                )
              },
              appendAmountAndDosage: ([name], state, { changeValue }) => {
                const currentState: any = { ...state.formState.values }
                const [amount, dosage] = [currentState.amount, currentState.dosage]
                changeValue(
                  state,
                  name,
                  (value) =>
                    value &&
                    value
                      .trim()
                      .concat(amount ? ` [${amount}]` : '')
                      .concat(dosage ? ` (${dosage})` : '')
                )
              },
            }}
            initialValues={initialProductValues}
            render={({
              values,
              handleSubmit,
              form: {
                mutators: { push, pop, update, clearRootSubType, titleCase, appendAmountAndDosage },
                getState,
              },
            }) => (
              <form
                className="ui form"
                onSubmit={handleSubmit}
                id="create-product-form"
                ref={createProductFormRef}
              >
                <div>
                  <div>
                    {storeCount === null ? (
                      <>
                        <Icon name="exclamation triangle" style={{ marginTop: '-0.8rem' }} />
                        Product availability unknown
                      </>
                    ) : (
                      <>
                        <Icon name="building" />
                        {storeCount} store(s)
                      </>
                    )}
                  </div>

                  <div>
                    {states ? (
                      <>
                        <Icon name="globe" style={{ marginTop: '-0.8rem' }} />
                        {states.join(', ')}
                      </>
                    ) : null}
                  </div>
                  {initialProductValues.store_id && (
                    <div style={{ marginTop: '10px' }}>
                      <Label circular color={'orange'}>
                        Store Published
                      </Label>
                    </div>
                  )}
                </div>
                <Form.Group
                  style={{
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    margin: '0 0 20px',
                  }}
                >
                  <div className="field">{getModeToggle()}</div>
                  {mode === 'edit' && isPostingProduct ? <Loader active inline /> : null}
                  {mode === 'edit'
                    ? {
                        null: (
                          <Form.Button
                            disabled={isPostingProduct}
                            onClick={(e) => {
                              e.preventDefault()
                              setRemoveConfirmation(true)
                            }}
                            type="button"
                            negative
                          >
                            Remove
                          </Form.Button>
                        ),
                        pending: (
                          <Form.Button
                            disabled={isPostingProduct}
                            onClick={(e) => {
                              e.preventDefault()
                              setRemoveConfirmation(true)
                            }}
                            type="button"
                            color="yellow"
                          >
                            Cancel removal
                          </Form.Button>
                        ),
                        removed: null,
                      }[removalStatus || 'null']
                    : null}
                  <Form.Button
                    disabled={isPostingProduct}
                    onClick={(e) => {
                      e.preventDefault()
                      if (mode === 'edit') {
                        getState().dirty
                          ? setUnsavedWarning(true)
                          : dispatch(
                              getAProduct({
                                productId: values.id,
                                mode: 'edit',
                              })
                            )
                      } else if (batchLookupStage === 'create') {
                        handleBatchModeCreateModalClose && handleBatchModeCreateModalClose()
                        dispatch(setInitialProductValues({ values: {} }))
                      } else {
                        dispatch(setInitialProductValues({ values: {} }))
                      }
                    }}
                    type="reset"
                  >
                    {mode === 'edit'
                      ? 'Refresh'
                      : batchLookupStage === 'create'
                      ? 'Close'
                      : 'Clear'}
                  </Form.Button>
                  <Form.Button
                    disabled={isPostingProduct}
                    loading={mode !== 'edit' && isPostingProduct}
                    primary
                    onClick={() => {
                      if (createProductFormRef.current !== null) {
                        createProductFormRef.current.requestSubmit()
                      }
                    }}
                    type="button"
                  >
                    {mode === 'edit' ? 'Save' : 'Create'}
                  </Form.Button>
                  <UnsavedWarningModal
                    unsavedWarning={unsavedWarning}
                    setUnsavedWarning={setUnsavedWarning}
                    dispatch={dispatch}
                    values={values}
                  />
                  <RemoveProductModal
                    open={removeConfirmation}
                    setOpen={setRemoveConfirmation}
                    onConfirm={() => {
                      dispatch(
                        removalStatus === null
                          ? initiateAProductRemoval({
                              productId: values.id,
                            })
                          : cancelAProductRemoval({
                              productId: values.id,
                            })
                      )
                    }}
                    removalStatus={removalStatus}
                    productId={values.id}
                  />
                </Form.Group>
                <Grid stackable>
                  <Grid.Column width={6}>
                    <Field name="name">
                      {(props) => (
                        <TextNumberInput
                          required
                          props={props}
                          label="Name"
                          placeholder="string"
                          fullWidth={true}
                          twoLine={true}
                          icon={
                            <>
                              <Icon
                                name="magic"
                                onClick={() => {
                                  titleCase('name')
                                }}
                              />
                              <Icon
                                name="paste"
                                onClick={() => {
                                  appendAmountAndDosage('name')
                                }}
                              />
                            </>
                          }
                        />
                      )}
                    </Field>

                    {getBrandField()}

                    <Field name="description">
                      {(props) => (
                        <Form.Field>
                          <label className="product-form-label">Description*</label>
                          <TextArea {...props.input} required rows="12" placeholder="string" />
                        </Form.Field>
                      )}
                    </Field>
                    <Form.Field inline>
                      <label className="product-form-label">Lineage</label>
                      <Field name="category" component="select">
                        <SelectionOptions
                          options={[
                            { ...{ id: '', key: '', text: '', value: '' } },
                            ...LINEAGE_OPTIONS,
                          ]}
                        />
                      </Field>
                    </Form.Field>
                    <Form.Field inline>
                      <label className="product-form-label">Product type*</label>
                      <Field name="kind" required>
                        {(props) => (
                          <select
                            {...props.input}
                            onChange={(e) => {
                              clearRootSubType()
                              props.input.onChange(e)
                            }}
                          >
                            <SelectionOptions options={PRODUCT_TYPE_OPTIONS} />
                          </select>
                        )}
                      </Field>
                    </Form.Field>
                    <Form.Field inline>
                      <label className="product-form-label">Product subtype</label>
                      <Field name="root_subtype" component="select">
                        {values.kind ? (
                          <>
                            <option value="" />
                            <SelectionOptions
                              options={PRODUCT_SUBTYPE_OPTIONS[values.kind as ProductKind]}
                            />
                          </>
                        ) : (
                          ''
                        )}
                      </Field>
                    </Form.Field>
                    <Field name="kind_subtype">
                      {(props) => (
                        <TextNumberInput
                          props={props}
                          label="Brand subtype"
                          placeholder="string"
                          fullWidth={true}
                        />
                      )}
                    </Field>
                  </Grid.Column>

                  <Grid.Column width={6}>
                    <Field name="amount">
                      {(props) => (
                        <TextNumberInput props={props} label="Amount" placeholder="string" />
                      )}
                    </Field>
                    <Field name="dosage">
                      {(props) => (
                        <TextNumberInput props={props} label="Dosage" placeholder="string" />
                      )}
                    </Field>
                    <Field name="cbd_dosage_milligrams" type="number">
                      {(props) => (
                        <TextNumberInput
                          props={props}
                          label="CBD dosage (mg)"
                          placeholder="number"
                          min="0"
                          step="0.01"
                        />
                      )}
                    </Field>
                    <Field name="thc_dosage_milligrams" type="number">
                      {(props) => (
                        <TextNumberInput
                          props={props}
                          label="THC dosage (mg)"
                          placeholder="number"
                          min="0"
                          step="0.01"
                        />
                      )}
                    </Field>
                    <Field name="net_weight_grams" type="number">
                      {(props) => (
                        <TextNumberInput
                          props={props}
                          label="Net weight (g)"
                          placeholder="number"
                          min="0"
                          step="0.01"
                        />
                      )}
                    </Field>
                    <Field name="quantity_value" type="number">
                      {(props) => (
                        <TextNumberInput
                          props={props}
                          label="Quantity value"
                          placeholder="number"
                          min="0"
                          step="0.01"
                        />
                      )}
                    </Field>
                    <Form.Field inline>
                      <label className="product-form-label">Quantity units</label>
                      <Field name="quantity_units" component="select">
                        <SelectionOptions options={QUANTITY_UNIT_OPTIONS} />
                      </Field>
                    </Form.Field>
                    <Form.Field>
                      <ArrayFieldLabelStyle>Product aliases</ArrayFieldLabelStyle>
                      {!isBatchLookup && (
                        <button
                          type="button"
                          style={{ marginLeft: 10, display: 'inline-block' }}
                          onClick={(e) => {
                            e.preventDefault()
                            push('aliases_attributes', { name: '' })
                          }}
                        >
                          <Icon name="add" size="small" />
                          Add
                        </button>
                      )}
                      {removalStatus != null ? (
                        <Message warning size="small" style={{ display: 'block' }}>
                          {removalStatus === 'removed'
                            ? 'This product is removed.'
                            : 'This product is pending removal.'}{' '}
                          Do not add new aliases.
                        </Message>
                      ) : null}
                      <FieldArray name="aliases_attributes">
                        {({ fields }) =>
                          fields
                            .map((name, index) => (
                              <Field name={`${name}[name]`} key={name}>
                                {(props) => (
                                  <Form.Field inline>
                                    <textarea
                                      rows={2}
                                      readOnly={isBatchLookup}
                                      style={{
                                        width: '90%',
                                        textDecorationLine: fields.value[index]['_destroy']
                                          ? 'line-through'
                                          : 'none',
                                      }}
                                      disabled={fields.value[index]['_destroy']}
                                      {...props.input}
                                      required
                                      placeholder="string"
                                    />
                                    {!isBatchLookup && (
                                      <Icon
                                        link
                                        name={
                                          fields.value[index]['_destroy']
                                            ? 'undo'
                                            : 'trash alternate outline'
                                        }
                                        onClick={() => {
                                          fields.value[index]['id'] != null
                                            ? update('aliases_attributes', index, {
                                                ...fields.value[index],
                                                _destroy: !fields.value[index]['_destroy'],
                                              })
                                            : fields.remove(index)
                                        }}
                                      />
                                    )}
                                  </Form.Field>
                                )}
                              </Field>
                            ))
                            .reverse()
                        }
                      </FieldArray>
                    </Form.Field>
                  </Grid.Column>

                  <Grid.Column width={4}>
                    <Tab
                      renderActiveOnly={false}
                      defaultActiveIndex={0}
                      panes={[
                        {
                          menuItem: 'Default Content',
                          pane: (
                            <Tab.Pane key={'default-content-key'}>
                              <Field name="image_urls">
                                {(props) => (
                                  <ImageList initialImages={initialProductValues.image_urls}>
                                    <ProductImages
                                      onChange={props.input.onChange}
                                      initialProductValues={initialProductValues}
                                    />
                                  </ImageList>
                                )}
                              </Field>
                            </Tab.Pane>
                          ),
                        },
                        {
                          menuItem: 'State-Specific',
                          pane: <Tab.Pane key={'state-specific-key'}>{localizations}</Tab.Pane>,
                        },
                      ]}
                    />

                    <div style={{ marginTop: '30px' }}>
                      <LocaleSpecificGridView productId={initialProductValues?.id} />
                    </div>
                  </Grid.Column>
                </Grid>
              </form>
            )}
          />
        )}
      </Segment>
    </ProductsSegmentStyle>
  )
}

const WrappedProducts = (props: ProductsProps) => {
  return <Products {...props} />
}

export default WrappedProducts
